import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { Dispatch, FC, SetStateAction, useState } from 'react';

import { toast } from 'react-toastify';
import { Close, Message } from '@mui/icons-material';
import { Application } from '@features/applications';
import DoneIcon from '@mui/icons-material/Done';
import CloseIcon from '@mui/icons-material/Close';
import { usePostJobOfferResponse } from '@features/applicant/hooks/usePostJobOfferResponse';
import DeclineJobOfferModal from '../DeclineJobOfferModal/DeclineJobOfferModal';
import { useGetSignedTemplates } from '@features/digitalOnboarding/hooks/useGetSignedTemplates';
import { formatTimestamp } from '@utils/formatTimestamp';
import MessagingModal from '@features/messaging/components/MessagingModal/MessagingModal';
import { createApplicationHistory } from '@features/applicant/hooks/createApplicationHistory';

type JobOfferResponseProps = {
  isOpen: boolean;
  handleClose: () => void;
  application: Application;
  setShowAcceptAccessModal: Dispatch<SetStateAction<boolean>>;
  setIsGetSuccessModal: Dispatch<SetStateAction<boolean>>;
};

const JobOfferResponse: FC<JobOfferResponseProps> = ({
  isOpen,
  handleClose,
  application,
  setShowAcceptAccessModal,
  setIsGetSuccessModal,
}) => {
  const [errorText, setErrorText] = useState('');
  const [showDeclineModal, setShowDeclineModal] = useState(false);
  const [isMessagingModalOpen, setIsMessagingModalOpen] = useState(false);

  const { signedTemplatesData, isSignedTemplatesLoading } =
    useGetSignedTemplates({
      applicationId: application.id,
      enabled: true,
    });
  createApplicationHistory({
    applicationId: application.id,
    type: 'JOB_OFFER_SEEN',
    enabled: Boolean(application.id),
  });
  const { responseJobOffer, isRespondingJobOffer } = usePostJobOfferResponse({
    setErrorText,
  });

  const handleSubmit = (response: string) => {
    const options = {
      onSuccess: (data: { status: number; message: string }) => {
        if (data.status === 200) {
          if (response === 'ACCEPT') {
            if (
              signedTemplatesData &&
              signedTemplatesData?.object?.length > 0
            ) {
              setShowAcceptAccessModal(true);
            } else {
              setIsGetSuccessModal(true);
            }
            toast.success(
              `You got the job at ${application?.job_listing?.company?.s_name}`,
            );
            handleClose();
          } else if (response === 'DECLINE') {
            toast.success(`Job Offer Declined`);
            handleClose();
          }
        }
      },
    };
    application.id &&
      responseJobOffer(
        { application_id: application.id, job_offer_response: response },
        options,
      );
  };

  return (
    <Dialog open={isOpen} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        <IconButton
          sx={{ position: 'absolute', top: 5, right: 5 }}
          onClick={handleClose}
        >
          <Close />
        </IconButton>
        <br />
        <Avatar
          src={application?.job_listing?.company?.s_logo_file || ''}
          style={{
            width: '65px',
            height: '65px',
            marginRight: '12px',
            borderRadius: 5,
            textAlign: 'center',
            margin: '0 auto',
          }}
        />
        {errorText && <Alert severity="error">{errorText}</Alert>}
        <Box textAlign="center" sx={{ p: 3 }}>
          <Typography variant="h4">
            {application.job_listing.company.s_name}
          </Typography>
          <Typography variant="body1">
            {application.job_listing.company.s_city}
            {application.job_listing.company.s_state},
            {application.job_listing.company.s_zip}
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} mb={2}>
          {application?.application_actions &&
            application?.application_actions?.interview_message && (
              <Typography
                sx={{
                  maxWidth: !application?.application_actions?.interview_message
                    ? '350px'
                    : 'auto',
                  textAlign: 'center',
                  alignSelf: 'center',
                }}
                textAlign="center"
                alignSelf="center"
                px={2}
                component="div" // so <p> tags render properly inside
                dangerouslySetInnerHTML={{
                  __html: application.application_actions.interview_message,
                }}
              />
            )}

          <Typography
            sx={{
              maxWidth: '350px',
              textAlign: 'center',
              alignSelf: 'center',
              margin: '8px 0 !important',
            }}
          >
            A job offer for the <b>{application?.position}</b>
            <br /> position was sent on{' '}
            <b>{formatTimestamp(application?.job_offered as string)}</b>.
          </Typography>
          <LoadingButton
            variant="contained"
            color="success"
            onClick={() => handleSubmit('ACCEPT')}
            startIcon={<DoneIcon />}
            loading={isRespondingJobOffer}
          >
            Accept Job Offer
          </LoadingButton>
          <Button
            variant="contained"
            color="error"
            onClick={() => setShowDeclineModal(true)}
            startIcon={<CloseIcon />}
          >
            Decline
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setIsMessagingModalOpen(true)}
            startIcon={<Message />}
          >
            Message
          </Button>
        </Stack>
      </DialogContent>
      {showDeclineModal && (
        <DeclineJobOfferModal
          isOpen={showDeclineModal}
          handleClose={() => setShowDeclineModal(false)}
          errorText={errorText}
          isRespondingJobOffer={isRespondingJobOffer}
          handleSubmit={handleSubmit}
        />
      )}
      <MessagingModal
        isOpen={isMessagingModalOpen}
        handleClose={() => setIsMessagingModalOpen(false)}
        application={application}
      />
    </Dialog>
  );
};

export default JobOfferResponse;
